import React, { useMemo, useState } from 'react';
import { FileBarChart, Printer, Download, Calendar, Truck, User, Factory, Container, Fuel, FileText, Search } from 'lucide-react';
import { PageShell, glassLight, primaryButton, ghostButton, inputStyle, EmptyState, StatCard, usePagination, Pagination, useIsMobile, T } from '../ui';
import { EMPRESA, SUPLIDORES, TIPOS_DISPONIBLES, esc, etiquetaTipo, fmtFecha, fmtGal, normalizarTipo, openPrintWindow, styleTag } from './pedidoDocumento';
import { csvCell, downloadText } from '../storage';
import { LOGO_DATA_URI } from '../Logo';
import type { DetalleAuditoria } from '../db';
import type { Camion, Cola, Conductor, Envasadora, Pedido, PedidoTipo } from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// REPORTES DE ÓRDENES
//
// Responde preguntas que el listado de Órdenes no puede: cuánto movió cada
// chofer el mes pasado, qué camión anda más, a qué envasadora se despachó más
// gas, cómo va cada suplidor.
//
// Todo sale de las órdenes ya guardadas: este módulo no crea ni cambia nada,
// solo lee, imprime y exporta.
//
// ── UNA SOLA TABLA, TRES SALIDAS ────────────────────────────────────────────
// Cada reporte se arma como un objeto `Tabla` (columnas + filas + totales), y
// de ahí salen la pantalla, el papel y el CSV. Si cada salida armara sus
// propias filas acabarían diciendo cosas distintas, que es el peor defecto
// posible en un reporte: el que lo lee no tiene cómo saber cuál miente.
// ─────────────────────────────────────────────────────────────────────────────

type Alineacion = 'izq' | 'der';
interface Columna {
  etiqueta: string;
  alineacion?: Alineacion;
  // Ancho relativo en el papel. Sin esto, una columna de nombres largos
  // aplasta las de números.
  ancho?: number;
  // Cómo se PINTA esa celda en pantalla (un distintivo de color, una barra…).
  // Es solo decoración: el dato sigue siendo el texto de `filas`, que es lo
  // que va al papel y al CSV. Así la pantalla puede ser bonita sin que las
  // tres salidas se separen.
  pintar?: (valor: string, fila: string[]) => React.ReactNode;
}
interface Tabla {
  columnas: Columna[];
  filas: string[][];
  // Fila de totales, con la misma cantidad de celdas que las columnas.
  totales?: string[];
}

// ── LOS REPORTES QUE HAY ────────────────────────────────────────────────────
type ReporteId = 'ordenes' | 'choferes' | 'camiones' | 'colas' | 'destinos' | 'suplidores';
const REPORTES: {
  id: ReporteId;
  titulo: string;
  icono: React.ElementType;
  ayuda: string;
}[] = [{
  id: 'ordenes',
  titulo: 'Órdenes',
  icono: FileText,
  ayuda: 'Una línea por orden, con todo su detalle.'
}, {
  id: 'choferes',
  titulo: 'Choferes',
  icono: User,
  ayuda: 'Cuánto movió cada conductor.'
}, {
  id: 'camiones',
  titulo: 'Camiones',
  icono: Truck,
  ayuda: 'Cuánto movió cada camión.'
}, {
  id: 'colas',
  titulo: 'Colas',
  icono: Container,
  ayuda: 'Cuánto movió cada cola.'
}, {
  id: 'destinos',
  titulo: 'Destinos',
  icono: Factory,
  ayuda: 'Cuánto se despachó a cada envasadora.'
}, {
  id: 'suplidores',
  titulo: 'Suplidores',
  icono: Fuel,
  ayuda: 'Refinería y Coastal, cada uno por su lado.'
}];

// ── RANGOS DE FECHA ─────────────────────────────────────────────────────────
// Los atajos cubren lo que se pide de verdad: lo de hoy, lo de la semana, el
// cierre del mes y el año. "Personalizado" aparece solo cuando se tocan las
// fechas a mano, para que el atajo activo nunca mienta.
const isoLocal = (d: Date) => {
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
};
type AtajoId = 'hoy' | 'semana' | 'mes' | 'mesPasado' | 'anio' | 'todo';
const ATAJOS: {
  id: AtajoId;
  label: string;
}[] = [{
  id: 'hoy',
  label: 'Hoy'
}, {
  id: 'semana',
  label: 'Esta semana'
}, {
  id: 'mes',
  label: 'Este mes'
}, {
  id: 'mesPasado',
  label: 'Mes pasado'
}, {
  id: 'anio',
  label: 'Este año'
}, {
  id: 'todo',
  label: 'Todo'
}];
function rangoDe(atajo: AtajoId): {
  desde: string;
  hasta: string;
} {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  if (atajo === 'todo') return {
    desde: '',
    hasta: ''
  };
  if (atajo === 'hoy') return {
    desde: isoLocal(hoy),
    hasta: isoLocal(hoy)
  };
  if (atajo === 'semana') {
    // La semana arranca el lunes: domingo (0) cuenta como el último día.
    const desdeLunes = (hoy.getDay() + 6) % 7;
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - desdeLunes);
    const domingo = new Date(lunes);
    domingo.setDate(lunes.getDate() + 6);
    return {
      desde: isoLocal(lunes),
      hasta: isoLocal(domingo)
    };
  }
  if (atajo === 'mes') {
    return {
      desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 1)),
      hasta: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0))
    };
  }
  if (atajo === 'mesPasado') {
    return {
      desde: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)),
      hasta: isoLocal(new Date(hoy.getFullYear(), hoy.getMonth(), 0))
    };
  }
  return {
    desde: isoLocal(new Date(hoy.getFullYear(), 0, 1)),
    hasta: isoLocal(new Date(hoy.getFullYear(), 11, 31))
  };
}
const galonesDe = (p: Pedido) => (p.lineas ?? []).reduce((s, l) => s + (l.galones || 0), 0);
const destinosDe = (p: Pedido) => (p.lineas ?? []).filter(l => l.destino).map(l => l.destino);
const ETIQUETA_ESTADO: Record<string, string> = {
  borrador: 'Borrador',
  autorizado: 'Autorizado',
  anulado: 'Anulado'
};

// ── ADORNOS DE PANTALLA ─────────────────────────────────────────────────────
// Solo cambian cómo se ve la celda; el dato es el mismo texto que va al papel.

// El suplidor con su color, igual que en el listado de Órdenes: de un vistazo
// se distingue Refinería de Coastal sin leer.
const COLOR_SUPLIDOR: Record<string, {
  color: string;
  soft: string;
}> = {
  'GLP (Refinería)': {
    color: T.brand,
    soft: T.accentSoft
  },
  'GLP (Coastal)': {
    color: T.info,
    soft: T.infoSoft
  },
  'WOP (derivados)': {
    color: T.textMuted,
    soft: T.surfaceSubtle
  }
};
const COLOR_ESTADO: Record<string, {
  color: string;
  soft: string;
}> = {
  Autorizado: {
    color: T.ok,
    soft: T.okSoft
  },
  Borrador: {
    color: T.warn,
    soft: T.warnSoft
  },
  Anulado: {
    color: T.danger,
    soft: T.dangerSoft
  }
};
function Distintivo({
  texto,
  paleta
}: {
  texto: string;
  paleta: Record<string, {
    color: string;
    soft: string;
  }>;
}) {
  const c = paleta[texto] ?? {
    color: T.textSecondary,
    soft: T.surfaceSubtle
  };
  return <span style={{
    display: 'inline-block',
    padding: '3px 9px',
    borderRadius: 99,
    fontSize: 11,
    fontWeight: 800,
    whiteSpace: 'nowrap',
    color: c.color,
    background: c.soft,
    border: `1px solid ${c.color}33`
  }} data-id="element-1094" data-mp="1pi5j5qcq7tou">
      {texto}
    </span>;
}

// Barra de proporción detrás del número: enseña de un golpe quién movió más
// sin tener que comparar cifras a mano.
function ConBarra({
  texto,
  parte
}: {
  texto: string;
  parte: number;
}) {
  return <span style={{
    position: 'relative',
    display: 'inline-block',
    minWidth: 96,
    paddingRight: 2
  }} data-id="element-1095" data-mp="6l0umodbfm8k">
      <span style={{
      position: 'absolute',
      right: 0,
      bottom: -3,
      height: 3,
      borderRadius: 2,
      width: `${Math.max(4, Math.round(parte * 100))}%`,
      background: T.brand,
      opacity: .5
    }} data-id="element-1096" data-mp="l7u0l30y3fo3" />

      <span style={{
      fontWeight: 800
    }} data-id="element-1097" data-mp="3oryfza3lz26q">{texto}</span>
    </span>;
}
const soloDigitos = (v: string) => Number(String(v).replace(/[^\d]/g, '')) || 0;

// Agrupa las órdenes por una clave y suma. Sirve igual para choferes, camiones,
// colas y destinos: lo único que cambia es de dónde sale la clave.
interface Grupo {
  clave: string;
  ordenes: number;
  galones: number;
  primera: string;
  ultima: string;
}
function agrupar(pedidos: Pedido[], claveDe: (p: Pedido) => string[]): Grupo[] {
  const mapa = new Map<string, Grupo>();
  for (const p of pedidos) {
    const claves = claveDe(p);
    if (!claves.length) continue;
    // Una orden con varios destinos cuenta una vez en cada uno, pero sus
    // galones se reparten para que el total de la columna siga cuadrando con
    // el total de las órdenes.
    const galones = galonesDe(p) / claves.length;
    for (const clave of claves) {
      const g = mapa.get(clave) ?? {
        clave,
        ordenes: 0,
        galones: 0,
        primera: p.fecha,
        ultima: p.fecha
      };
      g.ordenes += 1;
      g.galones += galones;
      if (p.fecha && (!g.primera || p.fecha < g.primera)) g.primera = p.fecha;
      if (p.fecha && (!g.ultima || p.fecha > g.ultima)) g.ultima = p.fecha;
      mapa.set(clave, g);
    }
  }
  return [...mapa.values()].sort((a, b) => b.galones - a.galones);
}
export function ReportesOrdenes({
  pedidos,
  conductores,
  camiones,
  colas,
  envasadoras,
  usuarioNombre,
  onLog
}: {
  pedidos: Pedido[];
  conductores: Conductor[];
  camiones: Camion[];
  colas: Cola[];
  envasadoras: Envasadora[];
  usuarioNombre: string;
  onLog?: (mensaje: string, detalle?: DetalleAuditoria) => void;
}) {
  const isMobile = useIsMobile();
  const [reporte, setReporte] = useState<ReporteId>('ordenes');
  const [atajo, setAtajo] = useState<AtajoId>('mes');
  const inicial = rangoDe('mes');
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [tipo, setTipo] = useState<'todos' | PedidoTipo>('todos');
  const [estado, setEstado] = useState<'todos' | 'borrador' | 'autorizado' | 'anulado'>('todos');
  const [incluirArchivadas, setIncluirArchivadas] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const aplicarAtajo = (id: AtajoId) => {
    const r = rangoDe(id);
    setAtajo(id);
    setDesde(r.desde);
    setHasta(r.hasta);
  };

  // Tocar una fecha a mano deja de coincidir con cualquier atajo: se marca
  // como personalizado en vez de dejar iluminado uno que ya no describe lo
  // que se está viendo.
  const cambiarFecha = (cual: 'desde' | 'hasta', valor: string) => {
    setAtajo('personalizado' as AtajoId);
    if (cual === 'desde') setDesde(valor);else setHasta(valor);
  };

  // ── LO QUE ENTRA EN EL REPORTE ────────────────────────────────────────
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return pedidos.filter(p => {
      if (!incluirArchivadas && p.archivado) return false;
      if (tipo !== 'todos' && normalizarTipo(p.tipo) !== tipo) return false;
      if (estado !== 'todos' && p.estado !== estado) return false;
      const f = p.fecha || '';
      if (desde && f < desde) return false;
      if (hasta && f > hasta) return false;
      if (!q) return true;
      return [p.numeroOrden, p.conductor, p.paseChofer, p.placaCamion, p.fichaCola, ...destinosDe(p)].join(' ').toLowerCase().includes(q);
    }).sort((a, b) => (b.fecha || '').localeCompare(a.fecha || ''));
  }, [pedidos, desde, hasta, tipo, estado, incluirArchivadas, busqueda]);

  // Las anuladas no suman galones: la orden existe en el historial pero no se
  // despachó. Sí se cuentan como órdenes, para que no parezca que se
  // desaparecieron.
  const totalGalones = useMemo(() => filtradas.filter(p => p.estado !== 'anulado').reduce((s, p) => s + galonesDe(p), 0), [filtradas]);
  const paraSumar = useMemo(() => filtradas.filter(p => p.estado !== 'anulado'), [filtradas]);

  // Catálogos para poner nombre a lo que en la orden es solo una placa o una
  // ficha. Si la unidad ya no está registrada se enseña el dato crudo, que es
  // mejor que un hueco.
  const nombreCamion = (placa: string) => {
    const c = camiones.find(x => x.placa === placa);
    return c ? `${c.nombre} (${c.placa})` : placa || '—';
  };
  const capacidadCola = (ficha: string) => {
    const c = colas.find(x => x.ficha === ficha);
    return c?.capacidadGalones ? `${fmtGal(c.capacidadGalones)} gal` : '—';
  };
  const rncDestino = (nombre: string) => envasadoras.find(e => e.nombre === nombre)?.rnc || '—';
  const paseDe = (nombre: string, p?: Pedido) => p?.paseChofer || conductores.find(c => c.nombre === nombre)?.pase || '—';

  // ── CADA REPORTE, COMO TABLA ──────────────────────────────────────────
  const tabla: Tabla = useMemo(() => {
    if (reporte === 'ordenes') {
      const filas = filtradas.map(p => [p.numeroOrden || '—', fmtFecha(p.fecha), etiquetaTipo(p.tipo), p.conductor || '—', p.paseChofer || '—', p.placaCamion || '—', p.fichaCola || '—', destinosDe(p).join(' · ') || '—', fmtGal(galonesDe(p)), ETIQUETA_ESTADO[p.estado] ?? p.estado, p.autorizadoPor || '—']);
      return {
        columnas: [{
          etiqueta: 'Orden',
          ancho: 7
        }, {
          etiqueta: 'Fecha',
          ancho: 8
        }, {
          etiqueta: 'Suplidor',
          ancho: 10,
          pintar: v => <Distintivo texto={v} paleta={COLOR_SUPLIDOR} data-id="element-1098" data-mp="cns49i9h5213" />
        }, {
          etiqueta: 'Conductor',
          ancho: 14
        }, {
          etiqueta: 'Pase',
          ancho: 7
        }, {
          etiqueta: 'Placa',
          ancho: 8
        }, {
          etiqueta: 'Cola',
          ancho: 7
        }, {
          etiqueta: 'Destino',
          ancho: 16
        }, {
          etiqueta: 'Galones',
          alineacion: 'der',
          ancho: 8,
          pintar: v => <span style={{
            fontWeight: 800
          }} data-id="element-1099" data-mp="ytujzr9iuw67">{v}</span>
        }, {
          etiqueta: 'Estado',
          ancho: 8,
          pintar: v => <Distintivo texto={v} paleta={COLOR_ESTADO} data-id="element-1100" data-mp="17vu4wunnqkef" />
        }, {
          etiqueta: 'Autorizó',
          ancho: 12
        }],
        filas,
        totales: ['', '', '', '', '', '', '', `${filtradas.length} ${filtradas.length === 1 ? 'orden' : 'órdenes'}`, fmtGal(totalGalones), '', '']
      };
    }
    if (reporte === 'suplidores') {
      const filas = [...TIPOS_DISPONIBLES, 'WOP' as PedidoTipo].map(t => {
        const suyas = filtradas.filter(p => normalizarTipo(p.tipo) === t);
        const suman = suyas.filter(p => p.estado !== 'anulado');
        const numeros = suyas.map(p => parseInt(String(p.numeroOrden ?? '').replace(/\D/g, ''), 10)).filter(n => Number.isFinite(n));
        return {
          t,
          suyas,
          fila: [SUPLIDORES[t].etiqueta, SUPLIDORES[t].razonSocial, String(suyas.length), String(suyas.filter(p => p.estado === 'autorizado').length), fmtGal(suman.reduce((s, p) => s + galonesDe(p), 0)), numeros.length ? String(Math.min(...numeros)) : '—', numeros.length ? String(Math.max(...numeros)) : '—']
        };
        // WOP solo si tiene algo en el rango: ya no se crean órdenes de ese
        // formato y una fila en cero todos los meses solo estorba.
      }).filter(x => x.t !== 'WOP' || x.suyas.length > 0).map(x => x.fila);
      return {
        columnas: [{
          etiqueta: 'Suplidor',
          ancho: 14,
          pintar: v => <Distintivo texto={v} paleta={COLOR_SUPLIDOR} data-id="element-1101" data-mp="3lrwy0rpyko2k" />
        }, {
          etiqueta: 'Razón social',
          ancho: 26
        }, {
          etiqueta: 'Órdenes',
          alineacion: 'der',
          ancho: 8
        }, {
          etiqueta: 'Autorizadas',
          alineacion: 'der',
          ancho: 10
        }, {
          etiqueta: 'Galones',
          alineacion: 'der',
          ancho: 10
        }, {
          etiqueta: 'Nº más bajo',
          alineacion: 'der',
          ancho: 10
        }, {
          etiqueta: 'Nº más alto',
          alineacion: 'der',
          ancho: 10
        }],
        filas,
        totales: ['Total', '', String(filtradas.length), String(filtradas.filter(p => p.estado === 'autorizado').length), fmtGal(totalGalones), '', '']
      };
    }

    // Los cuatro que se agrupan comparten forma; solo cambia de dónde sale la
    // clave y qué columna extra lleva.
    const config: Record<string, {
      etiqueta: string;
      extra: string;
      valorExtra: (clave: string) => string;
      claveDe: (p: Pedido) => string[];
    }> = {
      choferes: {
        etiqueta: 'Conductor',
        extra: 'Pase',
        valorExtra: c => paseDe(c),
        claveDe: p => p.conductor ? [p.conductor] : []
      },
      camiones: {
        etiqueta: 'Camión',
        extra: 'Placa',
        valorExtra: c => c,
        claveDe: p => p.placaCamion ? [p.placaCamion] : []
      },
      colas: {
        etiqueta: 'Cola',
        extra: 'Capacidad',
        valorExtra: capacidadCola,
        claveDe: p => p.fichaCola ? [p.fichaCola] : []
      },
      destinos: {
        etiqueta: 'Destino',
        extra: 'RNC',
        valorExtra: rncDestino,
        claveDe: destinosDe
      }
    };
    const c = config[reporte];
    const grupos = agrupar(paraSumar, c.claveDe);
    const filas = grupos.map(g => [reporte === 'camiones' ? nombreCamion(g.clave) : g.clave, c.valorExtra(g.clave), String(g.ordenes), fmtGal(Math.round(g.galones)), fmtFecha(g.primera), fmtFecha(g.ultima)]);
    // Las filas ya vienen de mayor a menor, así que el tope es la primera.
    const tope = grupos[0]?.galones || 1;
    return {
      columnas: [{
        etiqueta: c.etiqueta,
        ancho: 24,
        // El puesto va delante del nombre y solo en pantalla: en el papel la
        // fila ya está en orden y un número más solo gastaría tinta.
        pintar: (v, fila) => <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 9
        }} data-id="element-1102" data-mp="2wgr5f6llwqr5">
              <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 20,
            height: 20,
            borderRadius: 6,
            flexShrink: 0,
            fontSize: 10.5,
            fontWeight: 800,
            background: T.surfaceSubtle,
            color: T.textMuted
          }} data-id="element-1103" data-mp="1zr9rk4cgzyed">
                {filas.indexOf(fila) + 1}
              </span>
              <span style={{
            fontWeight: 700
          }} data-id="element-1104" data-mp="1gxnzbfp4av8o">{v}</span>
            </span>
      }, {
        etiqueta: c.extra,
        ancho: 12
      }, {
        etiqueta: 'Órdenes',
        alineacion: 'der',
        ancho: 9
      }, {
        etiqueta: 'Galones',
        alineacion: 'der',
        ancho: 11,
        pintar: v => <ConBarra texto={v} parte={soloDigitos(v) / (tope || 1)} data-id="element-1105" data-mp="oc7k8f3n585p" />
      }, {
        etiqueta: 'Primera',
        ancho: 10
      }, {
        etiqueta: 'Última',
        ancho: 10
      }],
      filas,
      totales: ['Total', '', String(grupos.reduce((s, g) => s + g.ordenes, 0)), fmtGal(Math.round(grupos.reduce((s, g) => s + g.galones, 0))), '', '']
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reporte, filtradas, paraSumar, totalGalones, camiones, colas, envasadoras, conductores]);
  const {
    page,
    setPage,
    totalPages,
    pageItems,
    rangeStart,
    rangeEnd,
    total
  } = usePagination(tabla.filas);
  const def = REPORTES.find(r => r.id === reporte)!;

  // Cómo se lee el rango en el papel y en la auditoría.
  const rangoTexto = !desde && !hasta ? 'Todas las fechas' : desde && hasta ? `Del ${fmtFecha(desde)} al ${fmtFecha(hasta)}` : desde ? `Desde el ${fmtFecha(desde)}` : `Hasta el ${fmtFecha(hasta)}`;
  const filtrosTexto = [tipo === 'todos' ? 'Todos los suplidores' : SUPLIDORES[tipo].etiqueta, estado === 'todos' ? 'Todos los estados' : ETIQUETA_ESTADO[estado], incluirArchivadas ? 'Incluye archivadas' : 'Sin archivadas', busqueda.trim() ? `Búsqueda: "${busqueda.trim()}"` : ''].filter(Boolean).join('  ·  ');

  // ── IMPRIMIR ──────────────────────────────────────────────────────────
  // Se imprime por el mismo camino que la orden de pedido (openPrintWindow),
  // y con el mismo membrete: es papel que sale de la empresa.
  const imprimir = () => {
    if (!tabla.filas.length) return;
    const css = `
      @page { size: letter landscape; margin: 1cm 1.1cm 1.4cm; }
      * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 9pt; color: #1a1207; margin: 0; }
      /* El margen de @page solo cuenta al imprimir. Sin esto, la vista previa
         que se abre en la pestaña sale pegada a los bordes y parece rota. */
      @media screen { body { padding: 1.1cm 1.3cm 2cm; background: #fff; } }

      /* Membrete: el mismo que el formulario de orden, para que se reconozca
         como papel de la misma casa. */
      .letterhead { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #1a1207; padding-bottom: 9px; }
      .letterhead img { height: 58px; width: auto; flex-shrink: 0; }
      .company { flex: 1; }
      .company .name { font-size: 12.5pt; font-weight: bold; text-transform: uppercase; letter-spacing: .01em; }
      .company .line { font-size: 8pt; line-height: 1.35; color: #4a443c; }
      .meta { text-align: right; font-size: 7.5pt; line-height: 1.5; color: #4a443c; white-space: nowrap; }
      .meta b { color: #1a1207; }

      /* Título con la franja naranja de la marca. */
      .titulo { display: flex; align-items: baseline; gap: 12px; margin: 14px 0 3px; padding-left: 11px; border-left: 5px solid #FF6B01; }
      .titulo h1 { font-size: 14pt; margin: 0; text-transform: uppercase; letter-spacing: .02em; }
      .titulo .rango { font-size: 10pt; font-weight: bold; color: #FF6B01; }
      .filtros { font-size: 7.5pt; color: #6b645a; margin: 0 0 12px 16px; }

      /* Resumen: los tres números que se miran primero. */
      .resumen { display: flex; gap: 10px; margin-bottom: 13px; }
      .caja { flex: 1; border: 1px solid #e2ddd6; border-radius: 7px; padding: 8px 12px; background: #faf9f7; }
      .caja .rot { font-size: 6.5pt; font-weight: bold; letter-spacing: .1em; text-transform: uppercase; color: #8a8175; }
      .caja .val { font-size: 15pt; font-weight: bold; line-height: 1.15; margin-top: 1px; }
      .caja.acento .val { color: #FF6B01; }

      table { width: 100%; border-collapse: collapse; }
      th { background: #1a1207; color: #fff; padding: 6px 7px; font-size: 7.5pt; text-align: left; text-transform: uppercase; letter-spacing: .05em; font-weight: bold; }
      th:first-child { border-top-left-radius: 5px; }
      th:last-child { border-top-right-radius: 5px; }
      td { border-bottom: 1px solid #e8e4de; padding: 5px 7px; font-size: 8.5pt; }
      tbody tr:nth-child(even) td { background: #faf9f7; }
      .der { text-align: right; }
      tfoot td { font-weight: bold; background: #f0ede8; border-top: 2px solid #1a1207; border-bottom: none; font-size: 9pt; padding: 7px; }

      /* Que la cabecera se repita en cada hoja y no se parta una fila. */
      thead { display: table-header-group; }
      tr { page-break-inside: avoid; }

      .pie { margin-top: 22px; padding-top: 7px; border-top: 1px solid #e2ddd6; font-size: 7pt; color: #6b645a; line-height: 1.5; display: flex; justify-content: space-between; gap: 20px; }

      /* Las firmas necesitan sitio de verdad: la raya sola, pegada a la fila de
         totales, no deja dónde poner la mano. Se separa del cuadro (margin-top)
         y encima de cada raya queda un renglón en blanco de 2 cm — la altura de
         una firma escrita a mano. Con page-break-inside: avoid, la hoja no se
         corta entre el renglón y su rótulo. */
      .firma { margin-top: 1.1cm; display: flex; gap: 1.6cm; page-break-inside: avoid; }
      .firma .campo { flex: 0 0 7cm; }
      .firma .renglon { height: 2cm; }
      .firma .raya { border-top: 1px solid #1a1207; padding-top: 5px; font-size: 7.5pt; text-align: center; color: #4a443c; }
      .no-print { margin-top: 18px; }
      .no-print button { font-family: inherit; font-size: 10pt; padding: 9px 18px; border-radius: 8px; border: 0; background: #FF6B01; color: #fff; font-weight: bold; cursor: pointer; }
      @media print { .no-print { display: none; } }
    `;
    const clase = (c: Columna) => c.alineacion === 'der' ? ' class="der"' : '';
    const heads = tabla.columnas.map(c => '<th' + clase(c) + (c.ancho ? ' style="width:' + c.ancho + '%"' : '') + '>' + esc(c.etiqueta) + '</th>').join('');
    const cuerpo = tabla.filas.map(f => '<tr>' + f.map((v, i) => '<td' + clase(tabla.columnas[i]) + '>' + esc(v) + '</td>').join('') + '</tr>').join('');
    const pie = tabla.totales ? '<tfoot><tr>' + tabla.totales.map((v, i) => '<td' + clase(tabla.columnas[i]) + '>' + esc(v) + '</td>').join('') + '</tr></tfoot>' : '';
    const impreso = new Date().toLocaleString('es-DO');

    // Los tres números que se miran primero, antes de leer una sola fila.
    const caja = (rot: string, val: string, acento = false) => '<div class="caja' + (acento ? ' acento' : '') + '">' + '<div class="rot">' + esc(rot) + '</div>' + '<div class="val">' + esc(val) + '</div></div>';
    const resumen = '<div class="resumen">' + caja('Órdenes en el rango', String(filtradas.length)) + caja('Galones', fmtGal(totalGalones), true) + caja('Autorizadas', String(filtradas.filter(p => p.estado === 'autorizado').length)) + caja(`Líneas · ${def.titulo}`, String(tabla.filas.length)) + '</div>';

    // El logo va incrustado como data URI, igual que en el formulario de
    // orden: así imprime aunque no haya conexión.
    const membrete = '<div class="letterhead">' + '<img src="' + LOGO_DATA_URI + '" alt="' + esc(EMPRESA.nombre) + '" />' + '<div class="company">' + '<div class="name">' + esc(EMPRESA.nombre) + '</div>' + '<div class="line">RNC ' + esc(EMPRESA.rnc) + '</div>' + '<div class="line">' + esc(EMPRESA.direccion) + '</div>' + '<div class="line">' + esc(EMPRESA.telefono) + ' · ' + esc(EMPRESA.email) + '</div>' + '</div>' + '<div class="meta"><b>Impreso</b><br />' + esc(impreso) + '<br /><b>Por</b><br />' + esc(usuarioNombre || '—') + '</div>' + '</div>';
    const campoFirma = (rotulo: string) => '<div class="campo">' + '<div class="renglon"></div>' + '<div class="raya">' + esc(rotulo) + '</div></div>';
    const firma = '<div class="firma">' + campoFirma('Preparado por') + campoFirma('Revisado por') + '</div>';
    const html = '<!doctype html><html lang="es"><head><meta charset="utf-8" />' + '<title>' + esc('Reporte de ' + def.titulo + ' · ' + EMPRESA.nombre) + '</title>' + styleTag(css) + '</head><body>' + membrete + '<div class="titulo"><h1>Reporte de ' + esc(def.titulo) + '</h1>' + '<span class="rango">' + esc(rangoTexto) + '</span></div>' + '<div class="filtros">' + esc(filtrosTexto) + '</div>' + resumen + '<table><thead><tr>' + heads + '</tr></thead><tbody>' + cuerpo + '</tbody>' + pie + '</table>' + firma + '<div class="pie"><span>' + (notaAnuladas ? esc(notaAnuladas) : '') + '</span>' + '<span>' + esc(`${tabla.filas.length} ${tabla.filas.length === 1 ? 'línea' : 'líneas'} · ${EMPRESA.nombre}`) + '</span></div>' + '<div class="no-print"><button onclick="window.print()">Imprimir / Guardar PDF</button></div>' + '</body></html>';
    openPrintWindow(html);
    onLog?.(`Reporte de ${def.titulo.toLowerCase()} impreso (${tabla.filas.length} líneas)`, {
      accion: 'imprimir',
      entidad: 'Reporte de órdenes',
      registro: def.titulo,
      cambios: [{
        campo: 'Rango',
        antes: '',
        despues: rangoTexto
      }, {
        campo: 'Filtros',
        antes: '',
        despues: filtrosTexto
      }, {
        campo: 'Líneas',
        antes: '',
        despues: String(tabla.filas.length)
      }]
    });
  };

  // ── EXPORTAR ──────────────────────────────────────────────────────────
  const exportar = () => {
    if (!tabla.filas.length) return;
    // El rango y los filtros van dentro del archivo: un CSV suelto en una
    // carpeta, sin eso, no se sabe de qué mes es.
    const cabecera = [[`Reporte de ${def.titulo}`], [rangoTexto], [filtrosTexto], ...(notaAnuladas ? [[notaAnuladas]] : []), []];
    const filas = [...cabecera, tabla.columnas.map(c => c.etiqueta), ...tabla.filas, ...(tabla.totales ? [tabla.totales] : [])];
    const csv = filas.map(f => f.map(csvCell).join(',')).join('\n');
    // BOM para que Excel abra bien los acentos.
    downloadText(`reporte-${def.titulo.toLowerCase()}.csv`, '﻿' + csv, 'text/csv;charset=utf-8');
    onLog?.(`Reporte de ${def.titulo.toLowerCase()} exportado a CSV (${tabla.filas.length} líneas)`, {
      accion: 'exportar',
      entidad: 'Reporte de órdenes',
      registro: def.titulo,
      cambios: [{
        campo: 'Rango',
        antes: '',
        despues: rangoTexto
      }, {
        campo: 'Líneas',
        antes: '',
        despues: String(tabla.filas.length)
      }]
    });
  };
  // Una orden anulada enseña sus galones en su fila pero NO suma en el total.
  // Sin decirlo, el que lee el papel ve una resta que no cuadra y piensa que
  // el reporte está mal. Solo se avisa si de verdad hay alguna.
  const anuladas = filtradas.filter(p => p.estado === 'anulado').length;
  const notaAnuladas = anuladas ? `${anuladas} ${anuladas === 1 ? 'orden anulada' : 'órdenes anuladas'} en el rango: se listan, pero sus galones no suman en el total.` : '';
  const hayDatos = tabla.filas.length > 0;

  // ── RENDER ────────────────────────────────────────────────────────────
  return <PageShell title="Reportes de Órdenes" sub="Resúmenes por rango de fechas, listos para imprimir" action={<div style={{
    display: 'flex',
    gap: 9,
    flexWrap: 'wrap'
  }} data-id="element-1107" data-mp="3lun0uyav41va">

          <button onClick={exportar} disabled={!hayDatos} style={{
      ...ghostButton,
      opacity: hayDatos ? 1 : .45,
      cursor: hayDatos ? 'pointer' : 'not-allowed'
    }} data-id="element-1108" data-mp="3gvslk6c3svfx">

            <Download size={14} data-id="element-1109" data-mp="2t452ubozwqoq" /> CSV
          </button>
          <button onClick={imprimir} disabled={!hayDatos} style={{
      ...primaryButton,
      opacity: hayDatos ? 1 : .45,
      cursor: hayDatos ? 'pointer' : 'not-allowed'
    }} data-id="element-1110" data-mp="2o864pwdv7ub4">

            <Printer size={15} data-id="element-1111" data-mp="2y512ue687sov" /> Imprimir
          </button>
        </div>} data-id="element-1106" data-mp="37vdxiz7r928q">

      {/* ── RESUMEN DEL RANGO ── */}
      <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0,1fr))',
      gap: 14,
      marginBottom: 22
    }} data-id="element-1112" data-mp="2aeonwjckcvos">

        <StatCard label="Órdenes" value={String(filtradas.length)} note="en el rango" data-id="element-1113" data-mp="13gcqq3xeqd2n" />
        <StatCard label="Galones" value={fmtGal(totalGalones)} note="sin las anuladas" accent={T.info} data-id="element-1114" data-mp="ypwdajtl6rga" />

        <StatCard label="Autorizadas" value={String(filtradas.filter(p => p.estado === 'autorizado').length)} note="firmadas" accent={T.ok} data-id="element-1115" data-mp="tqjqfz2ptkdp" />

        <StatCard label="Líneas del reporte" value={String(tabla.filas.length)} note={def.titulo.toLowerCase()} accent={T.warn} data-id="element-1116" data-mp="ot1cfu9uceyu" />

      </div>

      {/* ── QUÉ REPORTE ── */}
      <div style={{
      display: 'flex',
      gap: 8,
      flexWrap: 'wrap',
      marginBottom: 16
    }} data-id="element-1117" data-mp="1hg49sj2s6jni">

        {REPORTES.map(r => {
        const activo = reporte === r.id;
        const Icono = r.icono;
        return <button key={r.id} type="button" onClick={() => {
          setReporte(r.id);
          setPage(1);
        }} title={r.ayuda} style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '9px 15px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          color: activo ? T.brand : T.textSecondary,
          background: activo ? T.accentSoft : 'transparent',
          border: `1px solid ${activo ? `${T.brand}55` : T.border}`,
          transition: 'all .15s'
        }} data-id="element-1118" data-mp="18exrq5khh0s5">

              <Icono size={14} data-id="element-1119" data-mp="2mh58s529j6xn" />
              {r.titulo}
            </button>;
      })}
      </div>

      {/* ── RANGO DE FECHAS ── */}
      <div style={{
      ...glassLight,
      padding: isMobile ? 14 : 18,
      marginBottom: 18
    }} data-id="element-1120" data-mp="2wcyry7ahlyoe">

        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '.09em',
        textTransform: 'uppercase',
        color: T.textMuted,
        marginBottom: 11
      }} data-id="element-1121" data-mp="30etutg82gk4a">

          <Calendar size={13} data-id="element-1122" data-mp="1b05e52sg0g0g" /> Rango de fechas
        </div>

        <div style={{
        display: 'flex',
        gap: 7,
        flexWrap: 'wrap',
        marginBottom: 13
      }} data-id="element-1123" data-mp="1b0dw393fabii">

          {ATAJOS.map(a => {
          const activo = atajo === a.id;
          return <button key={a.id} type="button" onClick={() => aplicarAtajo(a.id)} style={{
            padding: '6px 13px',
            borderRadius: 99,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            fontFamily: 'inherit',
            color: activo ? T.brand : T.textSecondary,
            background: activo ? T.accentSoft : 'transparent',
            border: `1px solid ${activo ? `${T.brand}44` : T.border}`
          }} data-id="element-1124" data-mp="29dsvb8zx9i21">

                {a.label}
              </button>;
        })}
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, minmax(0,1fr))',
        gap: 12
      }} data-id="element-1125" data-mp="3203xup739t1h">

          <label style={{
          display: 'block'
        }} data-id="element-1126" data-mp="3g0oi7psnjwx3">
            <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            display: 'block',
            marginBottom: 5
          }} data-id="element-1127" data-mp="3b2d432vbr092">
              Desde
            </span>
            <input type="date" style={inputStyle} value={desde} onChange={e => cambiarFecha('desde', e.target.value)} data-id="element-1128" data-mp="3g157pwyuktw" />

          </label>
          <label style={{
          display: 'block'
        }} data-id="element-1129" data-mp="3pnqzf3s7y3nt">
            <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            display: 'block',
            marginBottom: 5
          }} data-id="element-1130" data-mp="3pm677ktdmwt4">
              Hasta
            </span>
            <input type="date" style={inputStyle} value={hasta} onChange={e => cambiarFecha('hasta', e.target.value)} data-id="element-1131" data-mp="31yg80cwrdlmc" />

          </label>
          <label style={{
          display: 'block'
        }} data-id="element-1132" data-mp="36ykpa55hqkdm">
            <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            display: 'block',
            marginBottom: 5
          }} data-id="element-1133" data-mp="2sy05b5nuaiij">
              Suplidor
            </span>
            <select style={inputStyle} value={tipo} onChange={e => {
            setTipo(e.target.value as typeof tipo);
            setPage(1);
          }} data-id="element-1134" data-mp="2pwey7xtkg8i9">

              <option value="todos" data-id="element-1135" data-mp="2g6c41uapkoic">Todos</option>
              {TIPOS_DISPONIBLES.map(t => <option key={t} value={t} data-id="element-1136" data-mp="2g494aijr7h0f">
                  {SUPLIDORES[t].etiqueta}
                </option>)}
              <option value="WOP" data-id="element-1137" data-mp="2b5en1l1fq0if">{SUPLIDORES.WOP.etiqueta}</option>
            </select>
          </label>
          <label style={{
          display: 'block'
        }} data-id="element-1138" data-mp="273b122h53diw">
            <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textMuted,
            display: 'block',
            marginBottom: 5
          }} data-id="element-1139" data-mp="224zq1of3ywz3">
              Estado
            </span>
            <select style={inputStyle} value={estado} onChange={e => {
            setEstado(e.target.value as typeof estado);
            setPage(1);
          }} data-id="element-1140" data-mp="1sar97xzfwsgr">

              <option value="todos" data-id="element-1141" data-mp="3mmxl6z05rda4">Todos</option>
              <option value="borrador" data-id="element-1142" data-mp="3v3ogi9lio7oi">Borrador</option>
              <option value="autorizado" data-id="element-1143" data-mp="3v2wthn5zji9u">Autorizado</option>
              <option value="anulado" data-id="element-1144" data-mp="2z46i9o4hlt6">Anulado</option>
            </select>
          </label>
        </div>

        <div style={{
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: 12
      }} data-id="element-1145" data-mp="7q2n7ad0wlwy">

          <div style={{
          position: 'relative',
          flex: '1 1 240px'
        }} data-id="element-1146" data-mp="cr9u777cdh2k">
            <Search size={15} style={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: T.textMuted
          }} data-id="element-1147" data-mp="3c6n6ubh0pje8" />

            <input style={{
            ...inputStyle,
            paddingLeft: 34
          }} placeholder="Buscar por orden, chofer, placa, cola o destino" value={busqueda} onChange={e => {
            setBusqueda(e.target.value);
            setPage(1);
          }} data-id="element-1148" data-mp="3l7ld70exi2zz" />

          </div>
          <label style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 12.5,
          color: T.textSecondary,
          cursor: 'pointer',
          fontWeight: 600
        }} data-id="element-1149" data-mp="15g7bb6zth7tq">

            <input type="checkbox" checked={incluirArchivadas} onChange={e => {
            setIncluirArchivadas(e.target.checked);
            setPage(1);
          }} style={{
            cursor: 'pointer'
          }} data-id="element-1150" data-mp="poofguy9qcge" />

            Incluir las archivadas
          </label>
        </div>
      </div>

      {/* ── LA TABLA ── */}
      {!hayDatos ? <EmptyState title="No hay nada en ese rango" hint="Prueba con otras fechas, otro suplidor, o quita el filtro de estado." icon={<FileBarChart size={22} data-id="element-1152" data-mp="6r3nhf07ixrk" />} data-id="element-1151" data-mp="3o7llwzn1ytqs" /> : <div style={{
      ...glassLight,
      overflow: 'hidden'
    }} data-id="element-1153" data-mp="1yalgyw4i3j6">

          <div style={{
        padding: '13px 18px',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 12,
        flexWrap: 'wrap'
      }} data-id="element-1154" data-mp="3ae42rfya3j1a">

            <span style={{
          fontSize: 13.5,
          fontWeight: 800,
          color: T.text
        }} data-id="element-1155" data-mp="1kvnpm0j1juec">
              {def.titulo}
            </span>
            <span style={{
          fontSize: 11.5,
          color: T.textMuted,
          fontWeight: 600
        }} data-id="element-1156" data-mp="1azklip4mplor">
              {rangoTexto}
            </span>
          </div>

          {/* La tabla se desplaza dentro de su caja: con once columnas, en el
           teléfono no cabe de ninguna manera, y es preferible arrastrarla a
           que empuje la página entera. */}
          {/* La cabecera se queda arriba al desplazar la tabla larga: con 40
           órdenes, sin esto hay que subir para recordar qué columna es
           cuál. */}
          <style data-id="element-1157" data-mp="1yqq3b9u4qo1p">{`
            .rep-th { position: sticky; top: 0; z-index: 1; background: rgba(255,252,248,.94); backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px); }
            .rep-fila { transition: background .12s; }
            .rep-fila:hover { background: ${T.surfaceHover} !important; }
          `}</style>

          <div style={{
        overflowX: 'auto',
        maxHeight: '70vh'
      }} data-id="element-1158" data-mp="19y7zvy3q8lnn">
            <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: isMobile ? 700 : undefined
        }} data-id="element-1159" data-mp="15wd233l08qem">

              <thead data-id="element-1160" data-mp="10yl8m54pow8b">
                <tr data-id="element-1161" data-mp="10zcv8qgbzjmg">
                  {tabla.columnas.map(c => <th key={c.etiqueta} className="rep-th" style={{
                textAlign: c.alineacion === 'der' ? 'right' : 'left',
                padding: '11px 14px',
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '.07em',
                textTransform: 'uppercase',
                color: T.textMuted,
                borderBottom: `1px solid ${T.border}`,
                whiteSpace: 'nowrap'
              }} data-id="element-1162" data-mp="1xo2g8pn98scn">

                      {c.etiqueta}
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1163" data-mp="2jvy1ubq3d2op">
                {pageItems.map((fila, i) => <tr key={i} className="rep-fila" style={{
              background: i % 2 ? T.surfaceSubtle : 'transparent'
            }} data-id="element-1164" data-mp="2jx9el11ilkry" data-mpi={i}>
                    {fila.map((v, j) => <td key={j} style={{
                textAlign: tabla.columnas[j]?.alineacion === 'der' ? 'right' : 'left',
                padding: '11px 14px',
                fontSize: 12.5,
                color: T.text,
                fontVariantNumeric: 'tabular-nums',
                borderBottom: `1px solid ${T.border}`,
                whiteSpace: j === 0 ? 'nowrap' : undefined
              }} data-id="element-1165" data-mp="2tudvomgi6q1w" data-mpi={j}>

                        {tabla.columnas[j]?.pintar ? tabla.columnas[j].pintar!(v, fila) : v}
                      </td>)}
                  </tr>)}
              </tbody>
              {tabla.totales ? <tfoot data-id="element-1166" data-mp="3lllcf8kcu7qf">
                  <tr data-id="element-1167" data-mp="3lmcwpn8i4j1e">
                    {tabla.totales.map((v, j) => <td key={j} style={{
                textAlign: tabla.columnas[j]?.alineacion === 'der' ? 'right' : 'left',
                padding: '12px 14px',
                fontSize: 13,
                fontWeight: 800,
                color: T.text,
                borderTop: `2px solid ${T.border}`,
                fontVariantNumeric: 'tabular-nums'
              }} data-id="element-1168" data-mp="3ljgydxsqrscf" data-mpi={j}>

                        {v}
                      </td>)}
                  </tr>
                </tfoot> : null}
            </table>
          </div>

          {notaAnuladas ? <div style={{
        padding: '11px 18px',
        fontSize: 11.5,
        color: T.textMuted,
        borderTop: `1px solid ${T.border}`,
        lineHeight: 1.5
      }} data-id="element-1169" data-mp="ghm4dq7u8cwj">
              {notaAnuladas}
            </div> : null}

          <div style={{
        padding: '4px 16px 14px'
      }} data-id="element-1170" data-mp="qdpat89q2xpa">
            <Pagination page={page} totalPages={totalPages} onChange={setPage} rangeStart={rangeStart} rangeEnd={rangeEnd} total={total} label="líneas" data-id="element-1171" data-mp="vf4x13nmza99" />

          </div>
        </div>}
    </PageShell>;
}